/** The six types the ticket editor offers. See TicketManagement.tsx:862-867. */
export type TicketQuestionType =
  | 'text'            // single-line input
  | 'essay'           // multi-line textarea
  | 'single_choice'   // radio group, answer is the chosen option
  | 'multiple_choice' // checkbox group, answer is comma-joined options
  | 'true_false'      // answer is the string 'true' or 'false'
  | 'file';           // answer is a stored file url

export interface QuestionValidation {
  minLength?: number;
  maxLength?: number;
}

export interface TicketQuestion {
  id: string;
  field_type: TicketQuestionType;
  label_ua: string;
  label_en: string | null;
  placeholder_ua: string | null;
  placeholder_en: string | null;
  is_required: boolean;
  validation_rules: QuestionValidation;
  field_options: string[];
  sort_order: number;
}

/**
 * A ticket_questions row as the organizer edits it: plus the ids of every
 * ticket it is linked to, and the standing "all tickets" rule.
 */
export type EditableQuestion = TicketQuestion & {
  ticketIds: string[];
  applies_to_all_tickets?: boolean;
  source_template_id?: string | null;
};

export interface QuestionAnswer {
  questionId: string;
  value: string | null;
  fileUrl: string | null;
}

/** Ukrainian is the primary language, so it is also the fallback. */
export function questionLabel(question: TicketQuestion, language: 'uk' | 'en'): string {
  if (language === 'en' && question.label_en) return question.label_en;
  return question.label_ua;
}

export function sortQuestions(questions: TicketQuestion[]): TicketQuestion[] {
  return [...questions].sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Returns a translation key describing the first problem with this answer, or
 * null when the answer is acceptable. Returning a key rather than a message
 * keeps this module free of language concerns.
 */
export function validateAnswer(
  question: TicketQuestion,
  answer: QuestionAnswer | undefined
): string | null {
  const isFile = question.field_type === 'file';
  const raw = answer?.value ?? null;
  const trimmed = raw === null ? null : raw.trim();
  const supplied = isFile ? !!answer?.fileUrl : !!trimmed;

  if (!supplied) {
    return question.is_required ? 'additionalFields.validation.required' : null;
  }

  // A file answer carries no text to validate further.
  if (isFile) return null;

  const value = trimmed as string;
  const rules = question.validation_rules ?? {};

  if (rules.minLength !== undefined && value.length < rules.minLength) {
    return 'ticketQuestions.errorTooShort';
  }
  if (rules.maxLength !== undefined && value.length > rules.maxLength) {
    return 'ticketQuestions.errorTooLong';
  }

  if (question.field_type === 'single_choice') {
    if (question.field_options.length > 0 && !question.field_options.includes(value)) {
      return 'ticketQuestions.errorInvalidOption';
    }
  }

  if (question.field_type === 'multiple_choice') {
    // Stored comma-joined by the registration form's checkbox handler.
    const chosen = value.split(',').map(v => v.trim()).filter(Boolean);
    const unknown = chosen.some(v => !question.field_options.includes(v));
    if (question.field_options.length > 0 && unknown) {
      return 'ticketQuestions.errorInvalidOption';
    }
  }

  if (question.field_type === 'true_false' && value !== 'true' && value !== 'false') {
    return 'ticketQuestions.errorInvalidOption';
  }

  return null;
}

export function validateAnswers(
  questions: TicketQuestion[],
  answers: QuestionAnswer[]
): Record<string, string> {
  const byId = new Map(answers.map(a => [a.questionId, a]));
  const errors: Record<string, string> = {};

  for (const question of questions) {
    const error = validateAnswer(question, byId.get(question.id));
    if (error) errors[question.id] = error;
  }

  return errors;
}

/**
 * Whether a question is asked on a ticket: linked to it, or standing - marked
 * for all tickets of its event, which reaches tickets created after it too.
 */
export function isAskedOnTicket(
  question: { applies_to_all_tickets?: boolean | null; ticketIds: string[] },
  ticketId: string
): boolean {
  return question.applies_to_all_tickets === true || question.ticketIds.includes(ticketId);
}

/**
 * The event's questions this ticket does not ask yet - asked on its other
 * tickets only. The ticket editor offers them so the organizer can ask one
 * here too; standing questions are asked everywhere and never listed.
 */
export function questionsOnOtherTickets<Q extends { ticketIds: string[]; applies_to_all_tickets?: boolean | null }>(
  questions: Q[],
  ticketId: string
): Q[] {
  return questions.filter(q => !isAskedOnTicket(q, ticketId));
}

/**
 * Which questions a ticket save should actually DELETE.
 *
 * Deleting a ticket_questions row is global and irreversible: it cascades the
 * links on every other ticket and every stored answer. So it is driven by what
 * the organizer removed in this editing session (`deletedThisSession`), never
 * by diffing the draft against the live list - the live list can gain a
 * question after the editor opened (a co-organizer, or the create-ticket flow
 * refetching), and such a question is absent from the draft without anyone
 * having asked for it to be destroyed. The two remaining conditions keep the
 * delete honest: the row must have existed before this session (a question
 * added and removed in the same session was never persisted), and it must
 * really be gone from the draft.
 */
export function questionIdsToDelete(
  deletedThisSession: Iterable<string>,
  persistedIds: Iterable<string>,
  draftIds: Iterable<string>
): string[] {
  const persisted = new Set(persistedIds);
  const draft = new Set(draftIds);
  return [...new Set(deletedThisSession)].filter(id => persisted.has(id) && !draft.has(id));
}

export interface ResponseRow {
  enrollment_id: string;
  ticket_id: string;
  question_id: string;
  field_value: string | null;
  file_url: string | null;
}

/** Blank answers are not stored, so "no row" unambiguously means "not answered". */
export function toResponseRows(
  answers: QuestionAnswer[],
  enrollmentId: string,
  ticketId: string
): ResponseRow[] {
  return answers
    .filter(a => (a.value !== null && a.value.trim() !== '') || !!a.fileUrl)
    .map(a => ({
      enrollment_id: enrollmentId,
      ticket_id: ticketId,
      question_id: a.questionId,
      field_value: a.value !== null && a.value.trim() !== '' ? a.value.trim() : null,
      file_url: a.fileUrl,
    }));
}

/**
 * The one shape uploadFile() ever produces:
 * `<uploaderUserId>/<ticketId>/<questionId>/<uuid>.<ext>` - four non-empty
 * segments over a deliberately tiny character set.
 *
 * This is an allowlist on purpose, not a blocklist. A first-segment
 * ownership check is worthless on its own because createSignedUrl
 * interpolates the stored key into the signed URL unencoded, and the WHATWG
 * URL parser then normalizes the result before the object is looked up, so
 * `<ownId>/../<victimId>/f.pdf` starts with the submitter's own id yet
 * resolves to the victim's object. Successive review rounds each found one
 * more spelling of that collapse that a "reject the bad forms" check missed:
 *
 *   `<ownId>/../<victimId>/f.pdf`       plain dot segments
 *   `<ownId>/..\<victimId>/f.pdf`       backslash is a separator on http(s)
 *   `<ownId>/%2e%2e/<victimId>/f.pdf`   `%2e` decodes to `.` before removal
 *   `<ownId>/.<TAB>./<victimId>/f.pdf`  tab, LF and CR are stripped from the
 *                                       whole URL before dot-segment removal
 *
 * All four are verified collapses against Node's URL. Rather than chase a
 * fifth, only plainly well-formed keys are accepted. The character set below
 * excludes every construct URL parsing can rewrite: no `%` (no
 * percent-decoding), no `\` and no `:` (no separator or scheme tricks), no
 * whitespace or control characters (nothing that is stripped before
 * parsing), and nothing outside ASCII. With `.` and `..` segments excluded
 * explicitly, a path that passes here is unchanged by URL normalization, so
 * the first segment the check reads is the first segment storage resolves.
 */
const TICKET_DOCUMENT_SEGMENT = '[A-Za-z0-9._-]+';
const TICKET_DOCUMENT_PATH = new RegExp(
  `^${TICKET_DOCUMENT_SEGMENT}/${TICKET_DOCUMENT_SEGMENT}/${TICKET_DOCUMENT_SEGMENT}/${TICKET_DOCUMENT_SEGMENT}$`
);

export function isWellFormedTicketDocumentPath(path: string): boolean {
  // The per-segment class excludes `/`, so this already pins the path to
  // exactly four non-empty segments and the whole string to
  // `^[A-Za-z0-9._/-]+$`.
  if (!TICKET_DOCUMENT_PATH.test(path)) return false;
  // `.` and `..` are the only remaining spellings the character set allows
  // that URL normalization would still act on.
  return path.split('/').every(segment => segment !== '.' && segment !== '..');
}

/**
 * The ticket-documents storage bucket's policies require an uploaded file's
 * path to be prefixed with the uploader's own user id, and the signed-URL
 * readback route (`/api/ticket-documents/[responseId]`) trusts that
 * invariant when it later signs the stored path with the service role -
 * which bypasses those storage policies entirely. A submitted fileUrl whose
 * first path segment isn't the submitting user's id would poison that
 * trust: an attacker enrolls as themselves but points fileUrl at another
 * user's document path, then legitimately requests their own response row
 * signed and receives the victim's file. Never store such a path.
 *
 * The prefix alone is not enough - see isWellFormedTicketDocumentPath for
 * why a path that starts with the right id can still resolve elsewhere.
 */
export function fileUrlOwnedBy(fileUrl: string, userId: string): boolean {
  if (!isWellFormedTicketDocumentPath(fileUrl)) return false;
  return fileUrl.split('/')[0] === userId;
}

/**
 * Applies fileUrlOwnedBy across a batch of answers. Answers whose fileUrl
 * fails the check are kept (so any non-file value on the same answer is not
 * lost) but with fileUrl nulled out, and reported in `rejected` so the
 * caller can log the offending value - mirrors filterAnswersToLinkedQuestions:
 * warn and continue, never fail the registration over this.
 */
export function stripUnownedFileUrls(
  answers: QuestionAnswer[],
  userId: string
): { answers: QuestionAnswer[]; rejected: Array<{ questionId: string; fileUrl: string }> } {
  const rejected: Array<{ questionId: string; fileUrl: string }> = [];

  const sanitized = answers.map(answer => {
    if (!answer.fileUrl || fileUrlOwnedBy(answer.fileUrl, userId)) return answer;
    rejected.push({ questionId: answer.questionId, fileUrl: answer.fileUrl });
    return { ...answer, fileUrl: null };
  });

  return { answers: sanitized, rejected };
}

/**
 * Server-side trust boundary: the client tells us which ticket it's registering
 * for and which questions it answered, but never trust that pairing blindly - a
 * crafted or stale request could attach an answer to a question that belongs to
 * a different ticket (or a different event entirely). Keep only answers whose
 * question is actually linked to this ticket right now; anything else is
 * reported as rejected so the caller can log it, never silently inserted.
 */
export function filterAnswersToLinkedQuestions(
  answers: QuestionAnswer[],
  allowedQuestionIds: Iterable<string>
): { kept: QuestionAnswer[]; rejected: string[] } {
  const allowed = new Set(allowedQuestionIds);
  const kept: QuestionAnswer[] = [];
  const rejected: string[] = [];

  for (const answer of answers) {
    if (allowed.has(answer.questionId)) {
      kept.push(answer);
    } else {
      rejected.push(answer.questionId);
    }
  }

  return { kept, rejected };
}

// ── Uploaded document limits ────────────────────────────────────────
//
// These mirror the `ticket-documents` storage bucket, which is the hard
// ceiling: it rejects anything larger or of another type regardless of what
// the client does. They live here so the organizer's editor, both participant
// forms and the upload's own pre-check all read one definition - a displayed
// rule that disagrees with the enforced one is worse than showing nothing.
//
// Bucket: file_size_limit 10485760, allowed_mime_types
// application/pdf, image/jpeg, image/png, image/heic

/** Maximum upload size in bytes, matching the bucket's file_size_limit. */
export const TICKET_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** The same limit in whole megabytes, for display. */
export const TICKET_DOCUMENT_MAX_MB = TICKET_DOCUMENT_MAX_BYTES / (1024 * 1024);

/** Value for a file input's `accept` attribute, so the OS picker filters. */
export const TICKET_DOCUMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic';

/** Human-readable format list, shown to organizers and participants. */
export const TICKET_DOCUMENT_FORMATS = 'PDF, JPG, PNG, HEIC';

/** True when the file is within the size the bucket will accept. */
export function isWithinTicketDocumentSize(fileSize: number): boolean {
  return fileSize <= TICKET_DOCUMENT_MAX_BYTES;
}

/**
 * The bucket's mime allowlist, mapped to the extension each type is normally
 * saved under. Kept in step with allowed_mime_types above.
 */
const TICKET_DOCUMENT_MIME_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
};

/**
 * The extension to store an upload under. The readback route only signs
 * plainly well-formed keys (isWellFormedTicketDocumentPath), whose character
 * set is ASCII - so a file named "паспорт.документ" cannot contribute its own
 * extension and used to land as ".bin", which tells the person who later
 * downloads it nothing about what the file is.
 *
 * The mime type is the better source anyway: it is what the file actually is
 * rather than what it happened to be named, the bucket has already rejected
 * anything outside its allowlist by the time this runs, and it survives names
 * that are entirely non-ASCII. The original extension still wins when it is
 * usable, so a plain "scan.pdf" is unaffected; "bin" remains only for the case
 * where neither source says anything.
 */
export function ticketDocumentExtension(fileName: string, mimeType: string | null | undefined): string {
  // Only look past a dot - "notes" has no extension, and split('.').pop()
  // would otherwise return the whole name as one.
  const rawExtension = fileName.includes('.') ? (fileName.split('.').pop() ?? '').trim() : '';
  // Partial ASCII matches are not usable either: "док2" would reduce to "2".
  if (/^[A-Za-z0-9]{1,10}$/.test(rawExtension)) return rawExtension.toLowerCase();

  return TICKET_DOCUMENT_MIME_EXTENSIONS[(mimeType ?? '').toLowerCase()] ?? 'bin';
}
